import React, { useState } from "react";
import LegalLayout from "./LegalLayout";
import { api, BRAND } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function DataDeletion() {
  const [form, setForm] = useState({ name: "", email: "", details: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.email.includes("@")) return toast.error("Please enter a valid email");
    setSubmitting(true);
    try {
      await api.post("/public/leads", {
        name: form.name || "Data deletion request",
        email: form.email,
        service: "data-deletion",
        message: form.details || "Please delete all personal information you hold about me.",
        source: "data-deletion",
      });
      setDone(true);
      toast.success("Request received — we'll be in touch within 30 days");
    } catch (err) {
      // Fallback to /api/leads if /public/leads not available
      try {
        await api.post("/leads", {
          name: form.name || "Data deletion request",
          email: form.email,
          service: "data-deletion",
          message: form.details || "Please delete all personal information you hold about me.",
          source: "data-deletion",
        });
        setDone(true);
        toast.success("Request received — we'll be in touch within 30 days");
      } catch {
        toast.error("Could not submit. Please email " + BRAND.email + " directly.");
      }
    } finally { setSubmitting(false); }
  };

  return (
    <LegalLayout
      title="Data Deletion Request"
      path="/data-deletion"
      subtitle="Request a copy of, or permanent deletion of, any personal information True North Maintenance holds about you."
      lastUpdated="13 May 2026"
    >
      <p>
        You have the right under the Australian Privacy Principles (APPs) and our internal policy
        to request the deletion of personal information we hold about you. This includes
        contact details from quote and contact forms, email subscriptions, and any reviews
        you have submitted via our website.
      </p>

      <h2>What we will do</h2>
      <ul>
        <li>Confirm your identity (we may ask for one additional piece of information, e.g. the property address the work was done at).</li>
        <li>Permanently delete the personal data within <strong>30 days</strong> of confirming your identity.</li>
        <li>Retain only information we are legally required to keep — e.g. invoices for tax purposes (7 years), or warranty records during the warranty period.</li>
        <li>Email you a confirmation when deletion is complete.</li>
      </ul>

      <h2>What we cannot delete</h2>
      <ul>
        <li>Information that you posted publicly on third-party platforms (Google reviews, Facebook comments) — you'll need to delete those directly with the platform.</li>
        <li>Financial records we are required to keep under Australian tax law.</li>
      </ul>

      <h2>Submit a request</h2>
      <p>The fastest way is the form below. You can also email <a href={`mailto:${BRAND.email}?subject=Data%20deletion%20request`}>{BRAND.email}</a> directly.</p>

      {done ? (
        <div className="not-prose mt-6 bg-cream border-l-4 border-navy p-6 rounded-sm">
          <div className="flex items-start gap-3">
            <CheckCircle size={24} weight="fill" className="text-navy shrink-0 mt-0.5"/>
            <div>
              <div className="font-display text-xl tracking-wide text-navy">Request received</div>
              <p className="text-sm text-slate-600 mt-1">We've logged your deletion request. You'll receive a confirmation email within 30 days. If you don't hear from us within 2 business days, please email {BRAND.email}.</p>
            </div>
          </div>
        </div>
      ) : (
        <form data-testid="data-deletion-form" onSubmit={submit} className="not-prose mt-6 bg-cream border border-slate-200 p-6 rounded-sm space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] font-medium text-slate-500">Your name (optional)</label>
            <Input data-testid="dd-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1 h-11"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] font-medium text-slate-500">Email associated with your account *</label>
            <Input data-testid="dd-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm mt-1 h-11"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] font-medium text-slate-500">Additional details (optional)</label>
            <Textarea data-testid="dd-details" rows={4} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} placeholder="e.g. property address you'd like removed, specific records to delete" className="rounded-sm mt-1"/>
          </div>
          <Button data-testid="dd-submit" type="submit" disabled={submitting} className="pill-navy !py-3 w-full justify-center !text-sm">{submitting ? "Submitting…" : "Submit deletion request"}</Button>
          <p className="text-[11px] text-slate-500 text-center">Your request will be processed under our <a href="/privacy" className="text-navy underline">Privacy Policy</a>.</p>
        </form>
      )}

      <h2>Contact</h2>
      <p>For any questions about data deletion, contact <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a> or {BRAND.phone}.</p>
    </LegalLayout>
  );
}
